/**
 * Service for the opt-in Optimist connection (shares the user's TOTP seed with Optimist).
 */

import { getApiEndpoints } from "../config/AuthConfigContext";
import { getStoredTokenData } from "./memberManagementService";

/** Result of an Optimist connection request. */
export interface OptimistConnectionResult {
  /** Connection state on success. */
  enabled?: boolean;
  /** HTTP status on failure (0 when the request never completed). */
  status?: number;
  /** Backend `detail` on failure. */
  detail?: string;
}

/**
 * Calls the backend Optimist connection endpoint with the caller's bearer token.
 * The user is identified by the token; no user id is sent.
 *
 * @param method - `GET` reads state, `POST` enables, `DELETE` disables
 * @param code - Current authenticator code, sent on `POST` only
 * @returns The connection state, or the failure status and detail
 */
export const callOptimistConnection = async (
  method: "GET" | "POST" | "DELETE",
  code?: string,
): Promise<OptimistConnectionResult> => {
  const token = getStoredTokenData()?.token;
  if (!token) return { status: 401 };
  try {
    const response = await fetch(getApiEndpoints().OPTIMIST_CONNECTION, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(code !== undefined && { "Content-Type": "application/json" }),
      },
      ...(code !== undefined && { body: JSON.stringify({ code }) }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      return { status: response.status, detail: data?.detail };
    }
    return { enabled: Boolean(data?.enabled) };
  } catch {
    return { status: 0 };
  }
};
